import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import {
  MdOutlineSettingsBackupRestore,
  MdOutlineRule,
} from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import Modal from "../components/ui/Modal";
import Table from "components/ui/Table";
import FilterBar from "components/ui/FilterBar";
import PackageRuleEdit from "components/packageRule/Edit";
import PackageRuleView, { describeOwner } from "components/packageRule/View";
import {
  useGetPackageRulesQuery,
  useGetPackageOptionsQuery,
  useGetOrganizationOfficesQuery,
  useCreatePackageRuleMutation,
  useUpdatePackageRuleMutation,
  useDeletePackageRuleMutation,
  useRestorePackageRuleMutation,
} from "apis/packageRule";
import { useGetOrganizationsQuery } from "apis/organization";
import { useGetProductsQuery } from "apis/products";
import { formatPropertyType } from "utils/helperFunctions";
import moment from "moment-timezone";

// Package Rules — the recommended marketing package, as data.
//
// A property's type / bedrooms / finish picks one rule, and the rule names the
// products and packages to recommend. Rules are per office, because most
// offices work with several contractor companies and each office's rules name
// whichever company that office actually uses. A rule with no offices applies
// to the whole organization as a fallback. Agents' own packages and company
// packages are made in the app and listed here alongside the group's.

const OWNER_OPTIONS = [
  { value: "group", label: "Group rules" },
  { value: "company", label: "Company packages" },
  { value: "agent", label: "Agent packages" },
];

const describeBedrooms = (rule) => {
  const { minBedrooms: min, maxBedrooms: max } = rule;
  if (min == null && max == null) return "Any";
  if (min != null && max != null) return min === max ? `${min}` : `${min}–${max}`;
  if (min != null) return `${min}+`;
  return `Up to ${max}`;
};

const PackageRules = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState("active");

  // Top-of-page filters
  const [organizationFilter, setOrganizationFilter] = useState("");
  const [officeFilter, setOfficeFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");

  const pageSize = 10;

  const { data: organizations = [] } = useGetOrganizationsQuery();
  const { data: offices = [] } =
    useGetOrganizationOfficesQuery(organizationFilter);
  const { data: packages = [] } = useGetPackageOptionsQuery();
  const { data: allProducts = [] } = useGetProductsQuery({ type: "marketing" });

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetPackageRulesQuery();

  const { mutateAsync: createRule, isPending: isCreateLoading } =
    useCreatePackageRuleMutation();
  const { mutateAsync: updateRule, isPending: isUpdateLoading } =
    useUpdatePackageRuleMutation();
  const { mutate: deleteRule, isPending: isDeleteLoading } =
    useDeletePackageRuleMutation();
  const { mutate: restoreRule, isPending: isRestoreLoading } =
    useRestorePackageRuleMutation();

  const products = useMemo(
    () => (allProducts || []).filter((p) => !p.isDeleted),
    [allProducts],
  );

  // Offices to filter by. Narrowed to the chosen organization's, or every
  // office that appears on a rule when no organization is picked.
  const officeOptions = useMemo(() => {
    if (organizationFilter) {
      return (offices || []).map((o) => ({
        value: String(o._id),
        label: o.name,
      }));
    }
    const seen = new Map();
    (data || []).forEach((r) =>
      (r.offices || []).forEach((o) => {
        if (o?._id) seen.set(String(o._id), o.name);
      }),
    );
    return [...seen].map(([value, label]) => ({ value, label })).sort((a, b) =>
      (a.label || "").localeCompare(b.label || ""),
    );
  }, [organizationFilter, offices, data]);

  const filteredData = useMemo(
    () =>
      (data || []).filter((rule) => {
        if (viewMode === "active" ? rule.isDeleted : !rule.isDeleted)
          return false;

        if (
          organizationFilter &&
          String(rule.organizationId?._id || rule.organizationId) !==
            organizationFilter
        )
          return false;

        if (
          officeFilter &&
          !(rule.offices || []).some(
            (o) => String(o?._id || o) === officeFilter,
          )
        )
          return false;

        if (ownerFilter && describeOwner(rule).kind !== ownerFilter)
          return false;

        return true;
      }),
    [data, viewMode, organizationFilter, officeFilter, ownerFilter],
  );

  const hasActiveFilters =
    !!organizationFilter || !!officeFilter || !!ownerFilter;

  const clearFilters = () => {
    setOrganizationFilter("");
    setOfficeFilter("");
    setOwnerFilter("");
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Rule Name",
        isFilter: true,
        minWidth: "170px",
      },
      {
        accessorKey: "organizationId",
        header: "Organization",
        cell: (row) => (
          <span className="truncate inline-block max-w-[160px]">
            {row.organizationId?.name || "-"}
          </span>
        ),
        isFilter: true,
        minWidth: "150px",
      },
      {
        accessorKey: "owner",
        header: "Made By",
        cell: (row) => {
          const owner = describeOwner(row);
          return (
            <span
              className="truncate inline-block max-w-[200px]"
              title={row.userId?.email || owner.label}
            >
              {owner.label}
            </span>
          );
        },
        isFilter: false,
        minWidth: "170px",
      },
      {
        accessorKey: "offices",
        header: "Offices",
        cell: (row) => {
          const list = row.offices || [];
          if (row.userId || row.companyId) {
            return <span className="text-darkgray">-</span>;
          }
          if (!list.length) {
            return (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-lightgray text-darkgray">
                Organization-wide
              </span>
            );
          }
          const names = list.map((o) => o.name || String(o));
          return (
            <span
              className="truncate inline-block max-w-[200px]"
              title={names.join(", ")}
            >
              {names.join(", ")}
            </span>
          );
        },
        isFilter: false,
        minWidth: "180px",
      },
      {
        accessorKey: "bedrooms",
        header: "Bedrooms",
        cell: (row) => (
          <span className="font-medium">{describeBedrooms(row)}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "propertyTypes",
        header: "Property Types",
        cell: (row) =>
          row.propertyTypes?.length ? (
            <span>{row.propertyTypes.map(formatPropertyType).join(", ")}</span>
          ) : (
            <span className="text-darkgray">Any</span>
          ),
        isFilter: false,
        minWidth: "160px",
      },
      {
        accessorKey: "finishes",
        header: "Finishes",
        cell: (row) =>
          row.finishes?.length ? (
            <span className="capitalize">{row.finishes.join(", ")}</span>
          ) : (
            <span className="text-darkgray">Any</span>
          ),
        isFilter: false,
        minWidth: "130px",
      },
      {
        accessorKey: "recommends",
        header: "Recommends",
        cell: (row) => {
          const productCount = row.productIds?.length || 0;
          const packageCount = row.packageIds?.length || 0;
          return (
            <span className="font-medium">
              {productCount} product{productCount === 1 ? "" : "s"}
              {packageCount ? (
                <span className="text-darkgray">
                  {" "}
                  + {packageCount} package{packageCount === 1 ? "" : "s"}
                </span>
              ) : null}
            </span>
          );
        },
        isFilter: false,
        minWidth: "150px",
      },
      ...(viewMode === "archive"
        ? [
            {
              accessorKey: "deletedAt",
              header: "Deleted Date",
              cell: (row) => (
                <span className="text-sm">
                  {row.deletedAt
                    ? moment(row.deletedAt)
                        .tz("Australia/Sydney")
                        .format("D MMM YYYY")
                    : "-"}
                </span>
              ),
              isFilter: false,
            },
          ]
        : []),
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Rule"
            >
              <FiEdit2 fontSize={20} />
            </button>
            {viewMode === "active" ? (
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsDeleteOpen(true);
                }}
                className="delete-icon"
                title="Delete Rule"
              >
                <FiTrash2 fontSize={20} />
              </button>
            ) : (
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsRestoreOpen(true);
                }}
                className="view-icon"
                title="Restore Rule"
              >
                <MdOutlineSettingsBackupRestore fontSize={22} color="green" />
              </button>
            )}
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    [viewMode],
  );

  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading package rules: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedRule = data?.find((rule) => rule._id === selectedId);

  // The form carries its own organization picker, pre-filled from the filter
  // when one is set, so a rule can be added without filtering first.
  const handleCreateSubmit = async (formData) => {
    await createRule(formData);
    setIsAddOpen(false);
  };

  const handleEditSubmit = async (formData) => {
    await updateRule({ _id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleDelete = () => {
    deleteRule(selectedId, { onSuccess: () => setIsDeleteOpen(false) });
  };

  const handleRestore = () => {
    restoreRule(selectedId, { onSuccess: () => setIsRestoreOpen(false) });
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddRuleButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add Rule
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Package Rule"
        className="max-w-3xl"
      >
        <PackageRuleEdit
          initialData={{ organizationId: organizationFilter || "" }}
          organizations={organizations}
          products={products}
          packages={packages}
          isSaving={isCreateLoading}
          onSubmit={handleCreateSubmit}
          onCancel={() => setIsAddOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Package Rule"
        className="max-w-3xl"
      >
        <PackageRuleEdit
          key={selectedId}
          initialData={selectedRule}
          organizations={organizations}
          products={products}
          packages={packages}
          isSaving={isUpdateLoading}
          onSubmit={handleEditSubmit}
          onCancel={() => setIsEditOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Package Rule Details"
        className="max-w-3xl"
      >
        <PackageRuleView rule={selectedRule} />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Package Rule"
      >
        <div>
          <p className="mb-4">
            Delete "{selectedRule?.name}"? Properties that matched it will fall
            through to the next matching rule, or show no recommendation.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              No
            </button>
            <button
              onClick={handleDelete}
              className="black-button"
              disabled={isDeleteLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isRestoreOpen}
        onClose={() => setIsRestoreOpen(false)}
        title="Restore Package Rule"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to restore "{selectedRule?.name}"?
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsRestoreOpen(false)}
              className="gray-button"
              disabled={isRestoreLoading}
            >
              No
            </button>
            <button
              onClick={handleRestore}
              className="black-button"
              disabled={isRestoreLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto">
        {/* Stacks on a phone, sits on one row from sm up */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <div className="flex gap-2 bg-lightgray rounded-lg p-1 border border-mediumgray self-start">
            <button
              onClick={() => setViewMode("active")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "active"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <MdOutlineRule className="w-4 h-4" />
              <span>Active</span>
            </button>
            <button
              onClick={() => setViewMode("archive")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "archive"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <FiTrash2 className="w-4 h-4" />
              <span>Archive</span>
            </button>
          </div>

          {/* Organization picks whose rules these are; office narrows them. */}
          <FilterBar
            className="w-full sm:w-auto [&>div]:flex-1 [&>div]:min-w-0 sm:[&>div]:flex-none"
            showClear={hasActiveFilters}
            onClear={clearFilters}
            filters={[
              {
                key: "organization",
                value: organizationFilter,
                onChange: (next) => {
                  setOrganizationFilter(next);
                  setOfficeFilter("");
                },
                allLabel: "All Organizations",
                options: (organizations || []).map((o) => ({
                  value: String(o._id),
                  label: o.name,
                })),
                minWidth: "200px",
              },
              {
                key: "office",
                value: officeFilter,
                onChange: setOfficeFilter,
                allLabel: "All Offices",
                options: officeOptions,
                minWidth: "200px",
              },
              {
                key: "owner",
                value: ownerFilter,
                onChange: setOwnerFilter,
                allLabel: "Everyone's",
                options: OWNER_OPTIONS,
                minWidth: "180px",
              },
            ]}
          />
        </div>

        <Table
          title="Package Rules"
          columns={columns}
          data={filteredData}
          pageSize={pageSize}
          ButtonCmp={viewMode === "active" ? AddRuleButton : undefined}
          onRowClick={handleViewRowClick}
          emptyText="No rules found"
        />
      </div>
    </>
  );
};

export default PackageRules;
